import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { LinkDto } from '../../api/types.js';
import { useApi } from '../../api/ApiProvider.js';
import { useActiveClassroom } from '../../context/ActiveClassroomProvider.js';
import { useWorkedThrough } from '../../hooks/useWorkedThrough.js';
import styles from './Links.module.css';

/**
 * Live Links page: the curated list a teacher built before class, opened one at
 * a time during the day. Opening a link goes to a real browser tab (arbitrary
 * sites can't be reliably embedded); opened links move to a "done" section
 * until Reset — mirroring the YouTube playlist's worked-through flow.
 */
export function Links() {
  const api = useApi();
  const { active } = useActiveClassroom();

  const linksQuery = useQuery({
    queryKey: ['links', active?.id ?? 'none'],
    queryFn: () => api.links.listLinks(active!.id),
    enabled: active !== null,
  });
  const links = useMemo(() => linksQuery.data ?? [], [linksQuery.data]);

  const { done, markDone, reset, prune } = useWorkedThrough(active?.id, 'linksOpened');

  useEffect(() => {
    prune(links.map((l) => l.id));
  }, [links, prune]);

  if (!active) {
    return (
      <section className={styles.empty} role="status">
        No classroom selected.
      </section>
    );
  }

  const unopened = links.filter((l) => !done.has(l.id));
  const opened = links.filter((l) => done.has(l.id));

  function open(link: LinkDto) {
    window.open(link.url, '_blank', 'noopener');
    markDone(link.id);
  }

  return (
    <section className={styles.links} data-testid="links-page">
      <div className={styles.shell}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>Links</h1>
            <p className={styles.subtitle}>
              {unopened.length} to open · {opened.length} opened
            </p>
          </div>
          <button
            type="button"
            className={styles.actionButton}
            disabled={done.size === 0}
            onClick={reset}
            data-testid="links-reset"
          >
            Reset list
          </button>
        </header>

        {linksQuery.isLoading ? <p className={styles.notice}>Loading links…</p> : null}

        {!linksQuery.isLoading && links.length === 0 ? (
          <p className={styles.notice}>
            No links yet. Add some from this classroom&rsquo;s Links settings tab.
          </p>
        ) : null}

        {unopened.length > 0 ? (
          <div className={styles.grid} data-testid="links-unopened-grid">
            {unopened.map((link) => (
              <LinkCard key={link.id} link={link} onOpen={() => open(link)} />
            ))}
          </div>
        ) : null}

        {links.length > 0 && unopened.length === 0 ? (
          <p className={styles.notice}>All links opened. Reset the list to show them again.</p>
        ) : null}

        {opened.length > 0 ? (
          <details className={styles.opened} data-testid="links-opened-list">
            <summary>Opened links</summary>
            <div className={styles.openedList}>
              {opened.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  className={styles.openedButton}
                  onClick={() => open(link)}
                >
                  <Favicon link={link} />
                  <span className={styles.openedText}>
                    <strong>{link.title}</strong>
                    <span>{hostOf(link.url)}</span>
                  </span>
                </button>
              ))}
            </div>
          </details>
        ) : null}
      </div>
    </section>
  );
}

function LinkCard({ link, onOpen }: { link: LinkDto; onOpen: () => void }) {
  return (
    <button
      type="button"
      className={styles.card}
      onClick={onOpen}
      data-testid="link-card"
      title={link.url}
    >
      <Favicon link={link} large />
      <span className={styles.cardText}>
        <strong>{link.title}</strong>
        <span>{hostOf(link.url)}</span>
      </span>
      <span className={styles.openBadge}>Open ↗</span>
    </button>
  );
}

function Favicon({ link, large = false }: { link: LinkDto; large?: boolean }) {
  const cls = large ? `${styles.favicon} ${styles.faviconLarge}` : styles.favicon;
  return link.faviconUrl ? (
    <img className={cls} src={link.faviconUrl} alt="" loading="lazy" />
  ) : (
    <span className={cls} aria-hidden="true">
      🔗
    </span>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
